import { Icon } from '../../../../layout/Icon.jsx';
import { Pill } from '../../../../layout/Pill.jsx';
import { formatIQD, formatDateTime } from '../../../../shared/format.js';
import { refObject, refName, userName } from '../../helpers/refs.js';
import { useRevenue } from '../../hooks/useRevenues.js';

const SOURCE_LABEL = {
  manual: 'يدوي',
  sales: 'بيع تلقائي',
  session_exit: 'إنهاء جلسة',
  card_topup: 'تعبئة كارت',
  other: 'أخرى'
};

const METHOD_LABEL = {
  cash: 'نقدي',
  transfer: 'تحويل',
  external: 'خارجي'
};

function Field({ label, children, wide }) {
  return (
    <div style={wide ? { gridColumn: '1 / -1' } : undefined}>
      <div className="text-[11px] font-semibold text-ink-3 mb-0.5">{label}</div>
      <div className="text-[13px] font-semibold text-ink">{children}</div>
    </div>
  );
}

export function RevenueDetails({ revenue, onClose }) {
  const id = revenue ? revenue._id || revenue.id : null;
  const { data, isLoading } = useRevenue(id);
  // بيانات الصف تُعرض فوراً، وتفاصيل الخادم تحلّ مكانها عند وصولها.
  const r = data || revenue || {};
  const cat = refObject(r.category || r.categoryId);
  const source = r.source || 'manual';
  const method = r.paymentMethod || 'cash';
  // السطر المشتقّ (إيراد إنهاء جلسة) يأتي من دفتر حركات القاصة لا من مجموعة
  // الإيرادات، ويحمل الجلسة التي أُغلقت.
  const session = r.session || null;

  if (!revenue) return null;

  return (
    <div className="fixed inset-0 z-50 grid place-items-center p-4" style={{ background: 'rgba(0,0,0,.5)' }}>
      <div className="card w-[520px] max-w-full max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="card-h">
          <h3 className="text-[15px] font-bold flex-1">تفاصيل الإيراد</h3>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            <Icon name="x" size={14} />
          </button>
        </div>

        <div className="card-b space-y-3">
          <div className="flex items-baseline justify-between gap-3">
            <div className="text-[20px] font-extrabold" style={{ color: '#15803D' }}>
              +{formatIQD(r.amount || 0)}
            </div>
            <Pill variant={source === 'manual' ? 'info' : 'muted'}>{SOURCE_LABEL[source] || source}</Pill>
          </div>

          <div className="grid grid-cols-2 gap-3 pt-1">
            <Field label="التاريخ">{formatDateTime(r.date || r.createdAt) || '—'}</Field>
            <Field label="الفئة">
              <span className="inline-flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full flex-none" style={{ background: cat.color || '#22C55E' }} />
                {cat.name || '—'}
              </span>
            </Field>
            <Field label="طريقة الاستلام">{METHOD_LABEL[method] || method}</Field>
            <Field label="اسم القاصة">{refName(r.cashbox || r.cashboxId)}</Field>
            <Field label="مُنفّذ العملية">{userName(r.createdBy || r.createdById)}</Field>
            <Field label="وقت التسجيل">{formatDateTime(r.createdAt) || '—'}</Field>
            <Field label="الوصف" wide>{r.description || '—'}</Field>
            <Field label="ملاحظات" wide>{r.notes || '—'}</Field>
            {session ? (
              <>
                <Field label="الطفل">{session.childName || '—'}</Field>
                <Field label="بداية الجلسة">{formatDateTime(session.startTime) || '—'}</Field>
                <Field label="وقت الخروج">
                  {formatDateTime(session.actualExitTime || session.endTime) || '—'}
                </Field>
                <Field label="معرّف الجلسة">
                  <span className="text-[11.5px] text-ink-2">{session._id || '—'}</span>
                </Field>
              </>
            ) : null}
          </div>

          {r.derived ? (
            <div className="text-[11.5px] text-ink-3 bg-paper-2 rounded-[10px] p-2.5 leading-5">
              هذا السطر محسوب من دفتر حركات القاصة (تسوية وخروج) ولا يوجد كسطر إيراد
              مستقل — كي لا يُحتسب المال مرتين مع تسديد الفاتورة. لذلك لا يمكن تعديله.
            </div>
          ) : null}

          {isLoading ? <div className="text-[12px] text-ink-3">جاري تحميل التفاصيل...</div> : null}
        </div>

        <div className="card-h border-t border-b-0 flex justify-end gap-2">
          <button type="button" className="btn btn-primary" onClick={onClose}>إغلاق</button>
        </div>
      </div>
    </div>
  );
}
